import React, { useEffect, useState, useRef } from "react";
import { Sparkles, Play, Bookmark, X, ArrowRight, ArrowLeft, CheckCheck, Dumbbell, Video, Share2, Trash2, Loader2, Users, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, palette } from "@/lib/api";
import { Card } from "@/pages/bitepal/Home";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ENERGY = ["Low", "Medium", "High"];
const LEVELS = ["Beginner", "Intermediate", "Advanced"];
const FOCI = ["Full Body", "Core", "Legs", "Push", "Pull", "Cardio", "Mobility"];
const diffColor = { Beginner: palette.emerald, Intermediate: palette.blue, Advanced: palette.orange };

const MUSCLES = ["Full Body", "Push", "Pull", "Legs", "Core"];
const EQUIP = ["Bodyweight", "Dumbbells", "Bands", "Gym"];
const TIMES = [15, 30, 45, 60];

export default function Fitness() {
  const [muscle, setMuscle] = useState("Full Body");
  const [equip, setEquip] = useState("Bodyweight");
  const [time, setTime] = useState(30);
  const [routine, setRoutine] = useState(null);
  const [gen, setGen] = useState(false);
  const [saved, setSaved] = useState([]);
  const [player, setPlayer] = useState(null);
  const load = async () => { try { setSaved(await api("/workouts")); } catch {} };
  useEffect(() => { load(); }, []);

  const generate = async () => { setGen(true); setRoutine(null);
    try { setRoutine(await api("/workouts/generate", { method: "POST", body: { muscle_groups: [muscle], equipment: equip, duration_minutes: time } })); }
    catch (e) { toast.error(e.message); } finally { setGen(false); } };
  const saveRoutine = async () => { try { await api("/workouts", { method: "POST", body: routine }); load(); toast.success("Saved to routines"); } catch (e) { toast.error(e.message); } };

  const Pills = ({ opts, val, on, suffix }) => (
    <div className="flex flex-wrap gap-2">{opts.map((o) => <button key={o} onClick={() => on(o)} className={`px-3.5 py-2 rounded-full text-sm font-semibold border ${val === o ? "bg-[#651F33] border-[#651F33] text-[#F5F0E8]" : "bg-[#EAE1D2] border-[#E3D9C8] text-slate-500"}`}>{o}{suffix ? ` ${suffix}` : ""}</button>)}</div>
  );

  return (
    <div>
      <h1 className="text-3xl font-extrabold mb-5">Fitness</h1>
      <WorkoutVideos />
      <Card>
        <h2 className="font-bold text-lg">AI Workout Generator</h2>
        <p className="text-sm text-slate-500 font-semibold mt-4 mb-2">Focus</p><Pills opts={MUSCLES} val={muscle} on={setMuscle} />
        <p className="text-sm text-slate-500 font-semibold mt-4 mb-2">Equipment</p><Pills opts={EQUIP} val={equip} on={setEquip} />
        <p className="text-sm text-slate-500 font-semibold mt-4 mb-2">Time</p><Pills opts={TIMES} val={time} on={setTime} suffix="min" />
        <button data-testid="fitness-generate-button" onClick={generate} disabled={gen} className="w-full mt-5 py-3.5 rounded-full bg-[#651F33] text-[#F5F0E8] font-bold flex items-center justify-center gap-2 disabled:opacity-60"><Sparkles size={18} /> {gen ? "Generating…" : "Generate Workout"}</button>
      </Card>

      {routine && (
        <Card className="mt-4">
          <div className="flex justify-between items-center"><h3 className="text-lg font-extrabold">{routine.routine_name}</h3><span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#F97316]/20 text-[#F97316]">~{routine.estimated_calories_burned} kcal</span></div>
          <p className="text-sm text-slate-500 mt-1">{routine.split_type} · {routine.duration_minutes} min · {routine.exercises?.length} exercises</p>
          {routine.exercises?.map((ex, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-t border-[#E3D9C8] mt-2">
              <div className="w-7 h-7 rounded-lg bg-[#651F33]/15 text-[#651F33] flex items-center justify-center font-bold text-sm">{i + 1}</div>
              <div><p className="font-semibold text-sm">{ex.name}</p><p className="text-xs text-slate-500">{ex.sets} × {ex.reps} · {ex.target_muscle}</p></div>
            </div>
          ))}
          <div className="flex gap-2 mt-4">
            <button data-testid="fitness-save-button" onClick={saveRoutine} className="flex-1 py-2.5 rounded-full bg-[#EAE1D2] text-[#651F33] font-bold text-sm flex items-center justify-center gap-1.5"><Bookmark size={16} /> Save</button>
            <button data-testid="fitness-start-button" onClick={() => setPlayer(routine)} className="flex-1 py-2.5 rounded-full bg-[#651F33] text-[#F5F0E8] font-bold text-sm flex items-center justify-center gap-1.5"><Play size={16} /> Start</button>
          </div>
        </Card>
      )}

      {saved.length > 0 && <h2 className="font-bold text-lg mt-7">Saved Routines</h2>}
      {saved.map((w) => (
        <Card key={w.workout_id} className="mt-2 !p-4 flex justify-between items-center cursor-pointer" >
          <div><p className="font-semibold">{w.routine_name}</p><p className="text-xs text-slate-500">{w.duration_minutes} min · {w.exercises?.length} exercises</p></div>
          <button onClick={() => setPlayer(w)} className="text-[#651F33]"><Play size={28} /></button>
        </Card>
      ))}

      {player && <Player routine={player} onClose={() => setPlayer(null)} onDone={load} />}
    </div>
  );
}

function Player({ routine, onClose, onDone }) {
  const [idx, setIdx] = useState(0);
  const ex = routine.exercises?.[idx];
  const last = idx === (routine.exercises?.length || 0) - 1;
  const finish = async () => { try { await api("/workouts/log", { method: "POST", body: { routine_name: routine.routine_name, duration_minutes: routine.duration_minutes, calories_burned: routine.estimated_calories_burned || 0 } }); } catch {} onDone(); onClose(); toast.success("Workout logged! +25 XP"); };
  return (
    <div className="fixed inset-0 z-50 bg-[#F5F0E8] flex flex-col p-6">
      <button onClick={onClose} className="self-end text-slate-500"><X size={28} /></button>
      <p className="text-slate-500 text-sm font-semibold">Exercise {idx + 1} of {routine.exercises.length}</p>
      <div className="h-1.5 rounded-full bg-[#EAE1D2] mt-2 overflow-hidden"><div className="h-full bg-[#651F33]" style={{ width: `${((idx + 1) / routine.exercises.length) * 100}%` }} /></div>
      <div className="flex-1 flex flex-col items-center justify-center gap-3">
        <div className="w-40 h-40 rounded-3xl bg-white border border-[#E3D9C8] flex items-center justify-center"><Dumbbell size={60} className="text-[#651F33]" /></div>
        <h2 className="text-3xl font-extrabold text-center">{ex?.name}</h2>
        <p className="text-lg font-bold text-[#651F33]">{ex?.sets} sets × {ex?.reps}</p>
        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#3B82F6]/20 text-[#3B82F6]">{ex?.target_muscle}</span>
        {ex?.form_cues && <p className="text-slate-500 text-center max-w-sm">💡 {ex.form_cues}</p>}
        <p className="text-slate-400 text-sm">Rest {ex?.rest_seconds}s between sets</p>
      </div>
      <div className="flex gap-3">
        {idx > 0 && <button onClick={() => setIdx((i) => i - 1)} className="flex-1 py-4 rounded-full bg-[#EAE1D2] font-bold flex items-center justify-center gap-2"><ArrowLeft size={18} /> Back</button>}
        {last ? <button data-testid="fitness-finish-button" onClick={finish} className="flex-1 py-4 rounded-full bg-[#651F33] text-[#F5F0E8] font-bold flex items-center justify-center gap-2"><CheckCheck size={18} /> Finish Workout</button>
          : <button onClick={() => setIdx((i) => i + 1)} className="flex-1 py-4 rounded-full bg-[#651F33] text-[#F5F0E8] font-bold flex items-center justify-center gap-2">Next <ArrowRight size={18} /></button>}
      </div>
    </div>
  );
}

const VPill = ({ opts, val, on }) => (
  <div className="flex flex-wrap gap-2">{opts.map((o) => (
    <button key={o} onClick={() => on(o)} className={`px-3.5 py-2 rounded-full text-sm font-semibold border ${val === o ? "bg-[#651F33] border-[#651F33] text-[#F5F0E8]" : "bg-[#EAE1D2] border-[#E3D9C8] text-slate-500"}`}>{o}</button>
  ))}</div>
);

function VideoCard({ v, onShare, onDelete }) {
  return (
    <div data-testid={`wv-card-${v.video_id}`} className="bg-white border border-[#E3D9C8] rounded-2xl overflow-hidden">
      <video data-testid={`wv-player-${v.video_id}`} controls playsInline preload="metadata" className="w-full aspect-[9/16] max-h-80 bg-black object-cover"
        src={`${BACKEND}${v.url}`} />
      <div className="p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="font-bold text-sm truncate">{v.title}</p>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: (diffColor[v.difficulty] || palette.emerald) + "22", color: diffColor[v.difficulty] || palette.emerald }}>{v.difficulty}</span>
        </div>
        <p className="text-xs text-slate-500 mt-1 flex items-center gap-2">
          <span className="flex items-center gap-1"><Clock size={12} /> {v.duration_seconds}s</span>
          <span>· {v.focus}</span>{v.author_name && <span>· {v.author_name}</span>}
        </p>
        {(onShare || onDelete) && (
          <div className="flex gap-2 mt-3">
            {onShare && <button data-testid={`wv-share-${v.video_id}`} onClick={() => onShare(v)} className={`flex-1 py-2 rounded-full text-xs font-bold flex items-center justify-center gap-1 ${v.shared ? "bg-[#651F33] text-[#F5F0E8]" : "bg-[#EAE1D2] text-[#651F33]"}`}><Share2 size={14} /> {v.shared ? "Shared" : "Share"}</button>}
            {onDelete && <button data-testid={`wv-delete-${v.video_id}`} onClick={() => onDelete(v)} className="py-2 px-3 rounded-full bg-[#EAE1D2] text-slate-400 hover:text-[#F43F5E]"><Trash2 size={14} /></button>}
          </div>
        )}
      </div>
    </div>
  );
}

function WorkoutVideos() {
  const [energy, setEnergy] = useState("Medium");
  const [level, setLevel] = useState("Beginner");
  const [focus, setFocus] = useState("Full Body");
  const [gen, setGen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [mine, setMine] = useState([]);
  const [community, setCommunity] = useState([]);
  const cancelled = useRef(false);

  const load = async () => {
    try { const [m, c] = await Promise.all([api("/workout-videos"), api("/workout-videos/community")]); setMine(m); setCommunity(c); } catch { /* ignore */ }
  };
  useEffect(() => { load(); cancelled.current = false; return () => { cancelled.current = true; }; }, []);

  const poll = async (id) => {
    for (let i = 0; i < 40 && !cancelled.current; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      if (cancelled.current) return;
      let v;
      try { v = await api(`/workout-videos/${id}`); } catch { continue; }
      setCurrent(v);
      if (v.status === "READY") { toast.success("Your workout clip is ready!"); setGen(false); load(); return; }
      if (v.status === "FAILED") { toast.error(v.failure || "Generation failed"); setGen(false); return; }
    }
    setGen(false);
  };

  const generate = async () => {
    setGen(true); setCurrent(null);
    try {
      const v = await api("/workout-videos/generate", { method: "POST", body: { energy, fitness_level: level, focus } });
      setCurrent(v); toast.message("Generating your clip — this takes ~30s–2 min");
      poll(v.video_id);
    } catch (e) { toast.error(e.message); setGen(false); }
  };

  const share = async (v) => { try { const r = await api(`/workout-videos/${v.video_id}/share`, { method: "POST" }); toast.success(r.shared ? "Shared to community" : "Removed from community"); load(); } catch (e) { toast.error(e.message); } };
  const del = async (v) => { try { await api(`/workout-videos/${v.video_id}`, { method: "DELETE" }); if (current?.video_id === v.video_id) setCurrent(null); load(); } catch (e) { toast.error(e.message); } };

  return (
    <>
      <Card className="mb-4">
        <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-xl bg-[#651F33]/12 flex items-center justify-center"><Video size={18} className="text-[#651F33]" /></div>
          <div><h2 className="font-bold text-lg leading-tight">AI Workout Video</h2><p className="text-xs text-slate-500">Runway-generated demo clip, matched to you</p></div></div>
        <p className="text-sm text-slate-500 font-semibold mt-4 mb-2">Energy</p><VPill opts={ENERGY} val={energy} on={setEnergy} />
        <p className="text-sm text-slate-500 font-semibold mt-4 mb-2">Fitness level</p><VPill opts={LEVELS} val={level} on={setLevel} />
        <p className="text-sm text-slate-500 font-semibold mt-4 mb-2">Focus</p><VPill opts={FOCI} val={focus} on={setFocus} />
        <button data-testid="wv-generate-button" onClick={generate} disabled={gen} className="w-full mt-5 py-3.5 rounded-full bg-[#651F33] text-[#F5F0E8] font-bold flex items-center justify-center gap-2 disabled:opacity-60">
          {gen ? <><Loader2 size={18} className="animate-spin" /> Generating…</> : <><Video size={18} /> Generate Video</>}
        </button>

        {gen && (!current || current.status !== "READY") && (
          <div data-testid="wv-loading" className="mt-4 p-6 rounded-2xl bg-[#ECE4D6] border border-[#E3D9C8] flex flex-col items-center text-center">
            <Loader2 size={28} className="text-[#651F33] animate-spin" />
            <p className="font-bold mt-3">Bubba is filming your workout…</p>
            <p className="text-xs text-slate-500 mt-1">{current?.title || `${focus} · ${energy} Energy`} · usually 30s–2 min</p>
          </div>
        )}
        {current && current.status === "READY" && (
          <div className="mt-4 max-w-xs mx-auto"><VideoCard v={current} onShare={share} onDelete={del} /></div>
        )}
      </Card>

      {mine.length > 0 && (
        <>
          <h2 className="font-bold text-lg mt-6 mb-2">My Videos</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">{mine.map((v) => <VideoCard key={v.video_id} v={v} onShare={share} onDelete={del} />)}</div>
        </>
      )}

      {community.length > 0 && (
        <>
          <h2 className="font-bold text-lg mt-6 mb-2 flex items-center gap-2"><Users size={18} className="text-[#3B82F6]" /> Community Workouts</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">{community.map((v) => <VideoCard key={v.video_id} v={v} />)}</div>
        </>
      )}
    </>
  );
}

